import React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { generateToken } from "app/services/api/management";
import {
  createAccessSystemZone,
  getAccessSystemZoneById,
  getAccessSystemZones,
  updateAccessSystemZone,
} from "app/services/api/accessSystemZones";
import {
  getPosWorkspacesDocument,
  updatePosWorkspacesDocument,
} from "app/services/api/accessSystemPosWorkspaces";
import { normalizeWorkspacesForUi } from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";
import {
  listTurnstileGateDevicesFromPosWorkspaceDoc,
  makeZoneDeviceSelectionKey,
} from "./zoneEligibleDevicesFromPosWorkspaces";
import { getLoggedInCustomerId } from "./accessSystemLoggedInCustomer";
import {
  normalizeZoneDeviceEntryFromApi,
  normalizeZoneIsActive,
} from "./zoneApiDeviceShape";
import { ZONES_MANAGEMENT_BASE_PATH } from "./zonesConstants";
import ZoneForm from "./components/ZoneForm";

const ZONES_I18N = "pages.dsokDetails.accessSystem.zones";

function getEmptyZoneValues() {
  return {
    name: "",
    description: "",
    isActive: true,
    devices: [],
  };
}

function mapApiZoneToFormValues(apiData) {
  if (!apiData || typeof apiData !== "object") {
    return getEmptyZoneValues();
  }
  const devicesRaw = Array.isArray(apiData.devices) ? apiData.devices : [];
  const seen = new Set();
  const devices = [];
  for (const x of devicesRaw) {
    const row = normalizeZoneDeviceEntryFromApi(x);
    if (!row) continue;
    const k = makeZoneDeviceSelectionKey(row.workspaceId, row.deviceId);
    if (seen.has(k)) continue;
    seen.add(k);
    devices.push({
      deviceId: row.deviceId,
      workspaceId: row.workspaceId,
      deviceType: row.deviceType,
      isActive: row.isActive,
      device: row.device
        ? {
            ...row.device,
            isActive: row.isActive,
          }
        : undefined,
    });
  }
  return {
    name: apiData.name != null ? String(apiData.name) : "",
    description: apiData.description != null ? String(apiData.description) : "",
    isActive: normalizeZoneIsActive(apiData, true),
    devices,
  };
}

function buildZonePayload(values) {
  const devices = (values.devices || []).map((d) => {
    const isActive = normalizeZoneIsActive(d, true);
    const device =
      d.device && typeof d.device === "object"
        ? {
            ...d.device,
            uuid: String(d.device.uuid ?? d.deviceId ?? "").trim(),
            isActive,
          }
        : {
            uuid: String(d.deviceId ?? "").trim(),
            type: String(d.deviceType ?? "").trim(),
            isActive,
          };

    return {
      deviceId: String(d.deviceId ?? device.uuid ?? "").trim(),
      workspaceId: String(d.workspaceId).trim(),
      deviceType: String(d.deviceType ?? device.type ?? "").trim(),
      isActive,
      device,
    };
  });

  return {
    name: String(values.name ?? "").trim(),
    description: String(values.description ?? "").trim(),
    isActive: normalizeZoneIsActive(values, true),
    devices,
  };
}

function syncZoneDeviceStatusesIntoWorkspaces(workspacesData, zoneDevices) {
  const statusByKey = new Map(
    (zoneDevices || []).map((d) => [
      makeZoneDeviceSelectionKey(d.workspaceId, d.deviceId),
      normalizeZoneIsActive(d, true),
    ]),
  );
  if (statusByKey.size === 0) return { data: workspacesData, changed: false };

  let changed = false;
  const data = (workspacesData || []).map((ws) => {
    if (!ws || typeof ws !== "object") return ws;
    const workspaceId = String(ws.uuid ?? "").trim();
    if (!workspaceId || !Array.isArray(ws.devices)) return ws;

    let workspaceChanged = false;
    const devices = ws.devices.map((device) => {
      if (!device || typeof device !== "object") return device;
      const ids = [device.uuid, device.deviceId, device.id]
        .map((id) => String(id ?? "").trim())
        .filter(Boolean);
      const matchedId = ids.find((id) =>
        statusByKey.has(makeZoneDeviceSelectionKey(workspaceId, id)),
      );
      if (!matchedId) return device;

      const nextIsActive = statusByKey.get(
        makeZoneDeviceSelectionKey(workspaceId, matchedId),
      );
      if (device.isActive === nextIsActive) return device;
      workspaceChanged = true;
      changed = true;
      return { ...device, isActive: nextIsActive };
    });

    return workspaceChanged ? { ...ws, devices } : ws;
  });

  return { data, changed };
}

async function syncPosWorkspaceDeviceStatuses(values, customerId) {
  const zoneDevices = Array.isArray(values?.devices) ? values.devices : [];
  if (zoneDevices.length === 0) return;

  let doc = null;
  try {
    doc = await getPosWorkspacesDocument();
  } catch (_e) {
    doc = null;
  }

  if (!doc || !Array.isArray(doc?.data)) {
    const cid =
      customerId != null && String(customerId).trim() !== ""
        ? String(customerId).trim()
        : "";
    if (cid) {
      const accessToken = await generateToken(cid);
      if (accessToken) {
        doc = await getPosWorkspacesDocument({ accessToken });
      }
    }
  }

  const { data: workspacesData } = normalizeWorkspacesForUi(
    doc && typeof doc === "object" ? doc : {},
  );
  const { data, changed } = syncZoneDeviceStatusesIntoWorkspaces(
    workspacesData,
    zoneDevices,
  );
  if (!changed) return;

  await updatePosWorkspacesDocument(customerId, { data });
}

/**
 * Session JWT (customer portal) usually matches {@link DsokPosWorkspacesPage}.
 * `generateToken` + customer JWT can return an empty document while session has data — try session first.
 *
 * @param {string|null|undefined} loggedInCustomerId
 * @returns {Promise<ReturnType<typeof listTurnstileGateDevicesFromPosWorkspaceDoc>>}
 */
async function loadEligibleDevicesForZoneForm(loggedInCustomerId) {
  const count = (rows) => (Array.isArray(rows) ? rows.length : 0);
  let best = [];
  let sessionErr = null;

  try {
    const doc = await getPosWorkspacesDocument();
    best = listTurnstileGateDevicesFromPosWorkspaceDoc(doc);
  } catch (e) {
    sessionErr = e;
  }

  const cid =
    loggedInCustomerId != null && String(loggedInCustomerId).trim() !== ""
      ? String(loggedInCustomerId).trim()
      : "";

  if (count(best) === 0 && cid) {
    try {
      const accessToken = await generateToken(cid);
      if (accessToken) {
        const doc = await getPosWorkspacesDocument({ accessToken });
        const rows = listTurnstileGateDevicesFromPosWorkspaceDoc(doc);
        if (count(rows) > count(best)) best = rows;
      }
    } catch (_e) {
      /* ignore — session may still have been enough */
    }
  }

  if (count(best) === 0 && sessionErr) {
    throw sessionErr;
  }
  return best;
}

async function fetchZoneDetail(zoneId) {
  try {
    const d = await getAccessSystemZoneById(zoneId);
    if (d && typeof d === "object") return d;
  } catch (e) {
    if (e?.response?.status !== 404) {
      throw e;
    }
  }
  const all = await getAccessSystemZones();
  const found = (all || []).find(
    (x) => String(x._id ?? x.id) === String(zoneId),
  );
  return found || null;
}

const AccessSystemZoneFormPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const { zoneId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isCreate = location.pathname.endsWith("/new");

  const [detailLoading, setDetailLoading] = React.useState(() => !isCreate);
  const [loadError, setLoadError] = React.useState(false);
  const [detail, setDetail] = React.useState(null);

  const [posEligibleDevices, setPosEligibleDevices] = React.useState([]);
  const [devicesLoading, setDevicesLoading] = React.useState(true);
  const [devicesLoadError, setDevicesLoadError] = React.useState(false);

  const [saving, setSaving] = React.useState(false);

  const loggedInCustomerId = getLoggedInCustomerId(getAuthUser);

  React.useEffect(() => {
    let cancelled = false;
    setDevicesLoading(true);
    setDevicesLoadError(false);
    const customerId = loggedInCustomerId;

    (async () => {
      try {
        if (cancelled) return;
        const eligible = await loadEligibleDevicesForZoneForm(customerId);
        if (cancelled) return;
        setPosEligibleDevices(eligible);
        setDevicesLoadError(false);
      } catch (_e) {
        if (!cancelled) {
          setPosEligibleDevices([]);
          setDevicesLoadError(true);
          enqueueSnackbar(
            t(`${ZONES_I18N}.form.devicesLoadError`, {
              defaultValue: "Could not load POS workspace devices.",
            }),
            { variant: "warning" },
          );
        }
      } finally {
        if (!cancelled) setDevicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loggedInCustomerId, enqueueSnackbar, t]);

  React.useEffect(() => {
    if (isCreate || !zoneId) {
      setDetailLoading(false);
      setLoadError(false);
      setDetail(null);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setLoadError(false);
    setDetail(null);
    (async () => {
      try {
        const res = await fetchZoneDetail(zoneId);
        if (cancelled) return;
        if (res) {
          setDetail(res);
          setLoadError(false);
        } else {
          setDetail(null);
          setLoadError(true);
        }
      } catch (_e) {
        if (!cancelled) {
          setDetail(null);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCreate, zoneId]);

  const initialValues = React.useMemo(() => {
    if (isCreate) return getEmptyZoneValues();
    if (loadError || !detail) return getEmptyZoneValues();
    return mapApiZoneToFormValues(detail);
  }, [isCreate, loadError, detail]);

  const listTitle = t("sidebar.menuItem.accessSystemZones", {
    defaultValue: "Zones",
  });

  const titleNew = t(`${ZONES_I18N}.form.titleNew`, {
    defaultValue: "New zone",
  });

  const titleDetailFallback = t(`${ZONES_I18N}.form.titleDetail`, {
    defaultValue: "Edit zone",
  });

  const pageTitle = isCreate
    ? titleNew
    : detail?.name != null && String(detail.name).trim() !== ""
      ? String(detail.name)
      : titleDetailFallback;

  const loadFailedMessage = t(`${ZONES_I18N}.form.loadFailed`, {
    defaultValue: "Could not load zone.",
  });

  const saveFailedMessage = t(`${ZONES_I18N}.form.saveFailed`, {
    defaultValue: "Save failed.",
  });

  const handleSubmit = React.useCallback(
    async (values) => {
      if (!isCreate && loadError) return;
      setSaving(true);
      const payload = buildZonePayload(values);
      try {
        await syncPosWorkspaceDeviceStatuses(values, loggedInCustomerId);
        if (isCreate) {
          const created = await createAccessSystemZone(payload);
          enqueueSnackbar(
            t("notifications.savedSuccessfully", {
              defaultValue: "Saved successfully",
            }),
            { variant: "success" },
          );
          const newId =
            created?._id != null
              ? String(created._id)
              : created?.id != null
                ? String(created.id)
                : null;
          if (newId) {
            navigate(
              `${ZONES_MANAGEMENT_BASE_PATH}/${encodeURIComponent(newId)}`,
              { replace: true },
            );
          } else {
            navigate(ZONES_MANAGEMENT_BASE_PATH);
          }
        } else if (zoneId) {
          await updateAccessSystemZone(zoneId, payload);
          enqueueSnackbar(
            t("notifications.savedSuccessfully", {
              defaultValue: "Saved successfully",
            }),
            { variant: "success" },
          );
          navigate(ZONES_MANAGEMENT_BASE_PATH);
        }
      } catch (err) {
        const fromApi = err?.response?.data?.message;
        const detailMsg =
          typeof fromApi === "string" && fromApi.trim()
            ? fromApi.trim()
            : saveFailedMessage;
        enqueueSnackbar(detailMsg, { variant: "error" });
      } finally {
        setSaving(false);
      }
    },
    [
      enqueueSnackbar,
      isCreate,
      loadError,
      loggedInCustomerId,
      navigate,
      saveFailedMessage,
      t,
      zoneId,
    ],
  );

  const handleCancel = React.useCallback(() => {
    navigate(ZONES_MANAGEMENT_BASE_PATH);
  }, [navigate]);

  const showForm =
    isCreate || (zoneId && !detailLoading && detail != null && !loadError);

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "none" }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink
          component={Link}
          to="/customer/dsok"
          color="inherit"
          underline="hover"
        >
          {t("pages.pos.breadcrumbCustomer")}
        </MuiLink>
        <Link to="/customer/dsok/access-system/tickets-management">
          {t("sidebar.menuItem.accessSystem")}
        </Link>
        <Link to={ZONES_MANAGEMENT_BASE_PATH} color="inherit">
          {listTitle}
        </Link>
        <Typography color="text.primary">{pageTitle}</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          width: "100%",
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          p: 2,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          {pageTitle}
        </Typography>

        {loadError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadFailedMessage}
          </Alert>
        ) : null}

        {detailLoading && !isCreate && zoneId ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={240}
          >
            <CircularProgress />
          </Box>
        ) : null}

        {showForm ? (
          <ZoneForm
            key={isCreate ? "create" : zoneId}
            mode={isCreate ? "create" : "edit"}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            posEligibleDevices={posEligibleDevices}
            devicesLoading={devicesLoading}
            devicesLoadError={devicesLoadError}
            saving={saving}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default AccessSystemZoneFormPage;
